import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "@/data/site";

// Desktop dropdown for a nav item that has children.
// The parent label itself stays clickable (navigates to parent.path).
function NavDropdown({ link }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className="flex items-center">
        <NavLink
          to={link.path}
          data-testid={`nav-link-${link.path.replace("/", "")}`}
          className={({ isActive }) =>
            `relative pl-4 pr-1 py-2 text-sm font-medium transition-colors ${
              isActive ? "text-michma" : "text-slate-600 hover:text-michma-ink"
            }`
          }
        >
          {({ isActive }) => (
            <>
              {link.name}
              {isActive && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute left-4 right-1 -bottom-0.5 h-0.5 bg-michma rounded-full"
                />
              )}
            </>
          )}
        </NavLink>
        <ChevronDown
          className={`w-4 h-4 mr-3 text-slate-400 transition-transform duration-300 ${
            open ? "rotate-180 text-michma" : ""
          }`}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            data-testid={`nav-dropdown-${link.path.replace("/", "")}`}
            className="absolute left-0 top-full pt-3"
          >
            <div className="min-w-[200px] rounded-2xl border border-slate-100 bg-white shadow-[0_20px_50px_rgba(17,47,153,0.12)] p-2">
              {link.children.map((child) => (
                <NavLink
                  key={child.name}
                  to={child.path}
                  data-testid={`nav-link-${child.path.replace("/", "")}`}
                  className="block rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-michma-soft hover:text-michma-ink transition-colors"
                >
                  {child.name}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 bg-white/80 backdrop-blur-xl border-b ${
          scrolled
            ? "border-slate-100 shadow-[0_1px_30px_rgba(17,47,153,0.06)]"
            : "border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2.5 group"
          >
            <img src="/logoMichma.png" alt="Michma" className="h-20 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.external ? (
                <a
                  key={link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-helios-link"
                  className="group inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-michma hover:text-michma-hover transition-colors"
                >
                  {link.logo ? (
                    <img src={link.logo} alt={link.name} className="h-7 w-auto" />
                  ) : (
                    link.name
                  )}
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : link.children ? (
                <NavDropdown key={link.name} link={link} />
              ) : (
                <NavLink
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace("/", "")}`}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-michma" : "text-slate-600 hover:text-michma-ink"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.name}
                      {isActive && (
                        <motion.span
                          layoutId="nav-underline"
                          className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-michma rounded-full"
                        />
                      )}
                    </>
                  )}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="inline-flex items-center gap-1.5 bg-michma text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-michma-hover hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
            >
              Get involved
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden grid place-items-center w-11 h-11 rounded-xl border border-slate-200 text-michma-ink"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-white pt-24 px-6 lg:hidden"
          >
            <nav className="flex flex-col divide-y divide-slate-100">
              {NAV_LINKS.map((link, i) =>
                link.external ? (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between py-5 font-heading text-2xl font-semibold text-michma"
                  >
                    {link.logo ? (
                      <img src={link.logo} alt={link.name} className="h-8 w-auto" />
                    ) : (
                      link.name
                    )}
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                ) : (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className="py-5"
                  >
                    <Link
                      to={link.path}
                      onClick={() => setOpen(false)}
                      className="block font-heading text-2xl font-semibold text-michma-ink"
                    >
                      {link.name}
                    </Link>
                    {link.children && (
                      <div className="mt-3 ml-4 flex flex-col gap-1 border-l-2 border-michma-tint pl-4">
                        {link.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.path}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-sublink-${child.path.replace("/", "")}`}
                            className="py-1.5 text-lg font-medium text-slate-500 hover:text-michma transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
